import React from 'react';
import Dialog from 'pages/Dashboard/components/Dialog';
import noop from 'lodash/noop';
import {
  settingsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useNotify,
} from 'core/hooks';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import Button from 'pages/Dashboard/components/Button';
import Controller from 'core/components/FormController';
import Input from 'pages/Dashboard/components/Input';
import DisabledFormTooltip from 'core/components/DisabledFormTooltip';
import {
  CreateOrModifyTagDTO,
  TagDTO,
} from 'dtos';
import {
  useUpsertTag,
} from 'views/EMR/Settings/hooks';
import {
  getSubmitButtonText,
  isEmptyString,
} from '@populateui/utils';

export type TagDialogProps = CreateOrModifyTagDTO;

type Props = CreateOrModifyTagDTO & {
  onClose: () => void;
};

export default function TagDialog({
  onClose,
  ...tag
}: Props) {
  const isNew = isEmptyString(tag?.name);
  const formProps = useForm<TagDTO>({
    mode: 'onChange',
    defaultValues: tag ?? {
      name: '',
    },
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { isValid, isSubmitting },
  } = formProps;

  const { mutateAsync: upsertTag } = useUpsertTag();
  const notify = useNotify();

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: TagDTO) => {
    try {
      const { name, tagId } = data;
      await upsertTag({ name, tagId });
      notify({
        variant: 'success',
        message: isNew ? 'Tag created successfully' : 'Tag updated successfully',
      });
      settingsEventChannel.emit('onTagsChange');
      handleClose();
    } finally {
      reset(data, { keepIsSubmitted: false });
    }
  };

  return (
    <FormProvider {...formProps}>
      <Dialog
        title={`${isNew ? 'Create' : 'Edit'} Tag`}
        onClose={onClose}
        onConfirm={noop}
        className="populate-form"
        open
      >
        <div className="flex flex-col gap-form-row">
          <div>
            <Controller
              name="name"
              control={control}
              rules={{ required: 'Tag name is required' }}
              render={({ field, fieldState: { invalid } }) => (
                <Input
                  {...field}
                  error={invalid}
                  showErrorText={false}
                  id="tag-name"
                  label="Tag Name"
                  placeholder="Enter Tag Name"
                  inputProps={{ maxLength: 256 }}
                />
              )}
            />
          </div>
          <div className="flex flex-row-reverse gap-2 justify-between">
            <DisabledFormTooltip>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={!isValid || isSubmitting}
                onClick={handleSubmit(onSubmit)}
              >
                {getSubmitButtonText(isNew, isSubmitting)}
              </Button>
            </DisabledFormTooltip>
            <Button
              variant="outlined"
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Close
            </Button>
          </div>
        </div>
      </Dialog>
    </FormProvider>
  );
}
